// AppShell — Pattern B left rail (desktop) + mobile drawer/topbar.
// Ported from Tally's AppShell, stripped of finance/demo/CmdK/notifications.
// Single source of truth for the primary nav. Wraps the routed <Outlet/>.

import {
  useEffect,
  useState,
  type ComponentType,
  type SVGProps,
} from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { signOut } from "../lib/api";
import { useSession } from "./AuthGate";
import {
  IconActivity,
  IconCoach,
  IconConnections,
  IconDiet,
  IconFood,
  IconGoals,
  IconHeart,
  IconHelp,
  IconSettings,
  IconSleep,
  IconToday,
  IconTrends,
  IconWeight,
} from "./RailIcons";

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;
type Group = "daily" | "weekly" | "rare";
type RailItem = {
  to: string;
  label: string;
  icon: IconComponent;
  matchPrefix?: string;
  group: Group;
};

const RAIL_ITEMS: RailItem[] = [
  // Daily — the surfaces touched most often.
  { to: "/", label: "Today", icon: IconToday, group: "daily" },
  { to: "/notes", label: "Notes", icon: IconCoach, matchPrefix: "/notes", group: "daily" },
  { to: "/food", label: "Food", icon: IconFood, matchPrefix: "/food", group: "daily" },
  { to: "/diet", label: "Diet", icon: IconDiet, matchPrefix: "/diet", group: "daily" },
  { to: "/weight", label: "Weight", icon: IconWeight, matchPrefix: "/weight", group: "daily" },
  { to: "/activity", label: "Activity", icon: IconActivity, matchPrefix: "/activity", group: "daily" },
  { to: "/heart", label: "Heart", icon: IconHeart, matchPrefix: "/heart", group: "daily" },
  { to: "/sleep", label: "Sleep", icon: IconSleep, matchPrefix: "/sleep", group: "daily" },
  // Weekly — reviewed regularly, not every day.
  { to: "/trends", label: "Trends", icon: IconTrends, matchPrefix: "/trends", group: "weekly" },
  { to: "/coach", label: "Coach", icon: IconCoach, matchPrefix: "/coach", group: "weekly" },
  // Less often — setup + occasional.
  { to: "/goals", label: "Goals", icon: IconGoals, matchPrefix: "/goals", group: "rare" },
  { to: "/connections", label: "Connections", icon: IconConnections, matchPrefix: "/connections", group: "rare" },
  { to: "/help", label: "Help", icon: IconHelp, matchPrefix: "/help", group: "rare" },
  { to: "/settings", label: "Settings", icon: IconSettings, matchPrefix: "/settings", group: "rare" },
];

const GROUP_LABELS: Record<Group, string> = {
  daily: "Daily",
  weekly: "Weekly",
  rare: "Less often",
};

function SignOutFooter() {
  const user = useSession();
  return (
    <div className="border-t border-ink-line p-3">
      <p className="caption mb-2 truncate px-2" title={user.email}>
        {user.email}
      </p>
      <button
        type="button"
        className="rail-link w-full text-left"
        onClick={async () => {
          await signOut();
          // Full reload rather than a router navigation: it drops every page's
          // cached data along with the session.
          window.location.reload();
        }}
      >
        Sign out
      </button>
    </div>
  );
}

export function AppShell() {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the mobile drawer on navigation.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const isActive = (item: RailItem): boolean => {
    if (item.to === "/") return location.pathname === "/";
    return item.matchPrefix
      ? location.pathname.startsWith(item.matchPrefix)
      : location.pathname === item.to;
  };

  const grouped = (["daily", "weekly", "rare"] as Group[]).map((g) => ({
    group: g,
    items: RAIL_ITEMS.filter((i) => i.group === g),
  }));

  const railContent = (
    <>
      <div className="p-5 border-b border-ink-line">
        <Link
          to="/"
          className="font-display text-xl tracking-tight text-ink focus-visible:outline-none focus-visible:underline"
          aria-label="Health home"
        >
          Health<span className="text-accent">.</span>
        </Link>
      </div>

      <nav className="p-3 flex-1 text-sm overflow-y-auto" aria-label="Primary">
        {grouped.map(({ group, items }) => (
          <div key={group}>
            <div className="px-2 pt-3 pb-1 eyebrow">{GROUP_LABELS[group]}</div>
            {items.map((item) => {
              const active = isActive(item);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? "page" : undefined}
                  className={`rail-link ${active ? "rail-link-active" : ""}`}
                >
                  <Icon className={`shrink-0 ${active ? "text-accent" : ""}`} />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <SignOutFooter />
    </>
  );

  return (
    <>
      {/* Skip link for keyboard users. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:rounded-md focus:bg-ink focus:text-white focus:shadow-card-hover"
      >
        Skip to main content
      </a>

      {/* MOBILE TOP BAR — visible only on <md. */}
      <header className="md:hidden bg-canvas/85 border-b border-ink-line sticky top-0 z-30 backdrop-blur">
        <div className="px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] flex items-center justify-between">
          <Link
            to="/"
            className="font-display text-lg tracking-tight text-ink"
            aria-label="Health home"
          >
            Health<span className="text-accent">.</span>
          </Link>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="p-1.5 text-ink-muted hover:text-ink rounded-md"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </header>

      {/* MOBILE DRAWER — fixed overlay. */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-ink/50 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        >
          <aside
            className="bg-canvas w-[260px] h-screen flex flex-col shadow-card-hover"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Primary navigation"
          >
            {railContent}
          </aside>
        </div>
      )}

      {/* DESKTOP LAYOUT + main content. */}
      <div className="flex min-h-screen">
        <aside
          className="hidden md:flex w-[232px] shrink-0 border-r border-ink-line bg-canvas/60 flex-col sticky top-0 h-screen"
          aria-label="Primary navigation"
        >
          {railContent}
        </aside>

        <main
          id="main"
          key={location.pathname}
          className="flex-1 min-w-0 animate-fade-in px-4 md:px-8 py-6 pb-24 md:pb-10"
        >
          <Outlet />
        </main>
      </div>
    </>
  );
}
